import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format, parseISO } from 'date-fns'
import { gql } from '~/__generated__'
import { Button } from '~/ui/button'
import { Card, CardContent, CardHeader } from '~/ui/card'
import { Badge } from '~/ui/badge'
import { Input } from '~/ui/input'
import { Switch } from '~/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/ui/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/ui/dropdown-menu'
import {
  ArrowLeft,
  Calendar,
  Clock,
  Edit,
  MoreHorizontal,
  Plus,
  Trash2,
  Play,
  Pause,
  AlertTriangle,
  Volume2,
} from 'lucide-react'
import { voiceMessagesPath } from '~/common/paths'
import invariant from 'tiny-invariant'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { AudioPlayer } from './components/audio-player'

const VOICE_MESSAGE_QUERY = gql(`
  query VoiceMessageWithSchedules($id: ID!) {
    voiceMessage(id: $id) {
      id
      text
      displayName
      enabled
      ttsStatus
      audioUrl
      durationSeconds
      campaigns {
        id
        name
        nextOccurrence
        enabled
      }
    }
  }
`)

const VOICE_MESSAGE_SCHEDULES_QUERY = gql(`
  query VoiceMessageSchedules($voiceMessageId: ID!, $enabledOnly: Boolean) {
    voiceMessageSchedules(voiceMessageId: $voiceMessageId, enabledOnly: $enabledOnly) {
      id
      startDate
      endDate
      startTime
      endTime
      rrule
      forceInterrupt
      priority
      enabled
      displayName
      frequencyDescription
      durationMinutes
      nextOccurrence
      createdAt
      updatedAt
    }
  }
`)

const CREATE_VOICE_MESSAGE_SCHEDULE_MUTATION = gql(`
  mutation CreateVoiceMessageSchedule($input: CreateVoiceMessageScheduleInput!) {
    createVoiceMessageSchedule(input: $input) {
      voiceMessageSchedule {
        id
        startDate
        endDate
        startTime
        endTime
        rrule
        forceInterrupt
        priority
        enabled
        displayName
        frequencyDescription
        durationMinutes
        nextOccurrence
      }
      errors
    }
  }
`)

const UPDATE_VOICE_MESSAGE_SCHEDULE_MUTATION = gql(`
  mutation UpdateVoiceMessageSchedule($input: UpdateVoiceMessageScheduleInput!) {
    updateVoiceMessageSchedule(input: $input) {
      voiceMessageSchedule {
        id
        startDate
        endDate
        startTime
        endTime
        rrule
        forceInterrupt
        priority
        enabled
        displayName
        frequencyDescription
        durationMinutes
        nextOccurrence
      }
      errors
    }
  }
`)

const DELETE_VOICE_MESSAGE_SCHEDULE_MUTATION = gql(`
  mutation DeleteVoiceMessageSchedule($input: DeleteVoiceMessageScheduleInput!) {
    deleteVoiceMessageSchedule(input: $input) {
      success
      errors
    }
  }
`)

const scheduleFormSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().min(1, 'End time is required'),
  frequency: z.enum(['none', 'hourly', 'daily', 'weekly', 'monthly']).default('none'),
  interval: z.number().min(1).default(1),
  weekdays: z.array(z.string()).optional(),
  forceInterrupt: z.boolean().default(false),
  priority: z.number().min(1).max(1000).default(100),
  enabled: z.boolean().default(true),
})

type ScheduleFormValues = z.infer<typeof scheduleFormSchema>

const parseRRuleDefaults = (rrule?: string | null) => {
  if (!rrule) {
    return {
      frequency: 'none' as ScheduleFormValues['frequency'],
      interval: 1,
      weekdays: [] as string[],
    }
  }

  const normalized = rrule.replace(/^RRULE:/i, '')
  const parts = normalized.split(';').reduce<Record<string, string>>((acc, part) => {
    const [key, value] = part.split('=')
    if (key && value) acc[key] = value
    return acc
  }, {})

  const freq = (parts.FREQ || '').toLowerCase()
  const frequency: ScheduleFormValues['frequency'] = ['hourly', 'daily', 'weekly', 'monthly'].includes(freq)
    ? (freq as ScheduleFormValues['frequency'])
    : 'none'

  const parsedInterval = parseInt(parts.INTERVAL || '1', 10)
  const interval = Number.isFinite(parsedInterval) && parsedInterval > 0 ? parsedInterval : 1
  const weekdays = parts.BYDAY ? parts.BYDAY.split(',').filter(Boolean) : []

  return { frequency, interval, weekdays }
}

interface ScheduleFormProps {
  voiceMessageId: string
  schedule?: any
  onSuccess: () => void
  onCancel: () => void
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  voiceMessageId,
  schedule,
  onSuccess,
  onCancel,
}) => {
  const [createSchedule, { loading: creating }] = useSafeMutation(
    CREATE_VOICE_MESSAGE_SCHEDULE_MUTATION
  )
  const [updateSchedule, { loading: updating }] = useSafeMutation(
    UPDATE_VOICE_MESSAGE_SCHEDULE_MUTATION
  )

  const isEditing = !!schedule
  const loading = creating || updating
  const rruleDefaults = useMemo(() => parseRRuleDefaults(schedule?.rrule), [schedule?.rrule])

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      startDate: schedule?.startDate || '',
      endDate: schedule?.endDate || '',
      startTime: schedule?.startTime || '09:00',
      endTime: schedule?.endTime || '09:01',
      frequency: rruleDefaults.frequency,
      interval: rruleDefaults.interval,
      weekdays: rruleDefaults.weekdays,
      forceInterrupt: schedule?.forceInterrupt || false,
      priority: schedule?.priority || 100,
      enabled: schedule?.enabled ?? true,
    },
  })

  const generateRRule = (values: ScheduleFormValues): string | undefined => {
    if (values.frequency === 'none') return undefined

    const parts = ['RRULE:']

    switch (values.frequency) {
      case 'hourly':
        parts.push(`FREQ=HOURLY`)
        if (values.interval > 1) {
          parts.push(`;INTERVAL=${values.interval}`)
        }
        break
      case 'daily':
        parts.push(`FREQ=DAILY`)
        if (values.interval > 1) {
          parts.push(`;INTERVAL=${values.interval}`)
        }
        break
      case 'weekly':
        parts.push(`FREQ=WEEKLY`)
        if (values.interval > 1) {
          parts.push(`;INTERVAL=${values.interval}`)
        }
        if (values.weekdays?.length) {
          parts.push(`;BYDAY=${values.weekdays.join(',')}`)
        }
        break
      case 'monthly':
        parts.push(`FREQ=MONTHLY`)
        if (values.interval > 1) {
          parts.push(`;INTERVAL=${values.interval}`)
        }
        break
    }

    return parts.join('')
  }

  const onSubmit = async (values: ScheduleFormValues) => {
    const scheduleInput = {
      voiceMessageId,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
      startTime: values.startTime,
      endTime: values.endTime,
      rrule: generateRRule(values),
      forceInterrupt: values.forceInterrupt,
      priority: values.priority,
      enabled: values.enabled,
    }

    try {
      if (isEditing) {
        await updateSchedule({
          variables: {
            input: {
              id: schedule.id,
              input: scheduleInput,
            },
          },
          refetchQueries: [VOICE_MESSAGE_SCHEDULES_QUERY as any],
        })
      } else {
        await createSchedule({
          variables: {
            input: {
              input: scheduleInput,
            },
          },
          refetchQueries: [VOICE_MESSAGE_SCHEDULES_QUERY as any],
        })
      }
      onSuccess()
    } catch (error) {
      console.error('Error saving schedule:', error)
    }
  }

  return (
    <div className="max-h-[80vh] overflow-y-auto p-1">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Time Configuration */}
          <div className="space-y-4">
            <div className="border-b pb-2">
              <h3 className="text-lg font-medium">Time Configuration</h3>
              <p className="text-muted-foreground text-sm">Set when the message should play</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Start Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">End Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Frequency Configuration */}
          <div className="space-y-4">
            <div className="border-b pb-2">
              <h3 className="text-lg font-medium">Frequency</h3>
              <p className="text-muted-foreground text-sm">How often should this message repeat</p>
            </div>

            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Repeat Pattern</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">One-time only</SelectItem>
                      <SelectItem value="hourly">Every hour</SelectItem>
                      <SelectItem value="daily">Every day</SelectItem>
                      <SelectItem value="weekly">Every week</SelectItem>
                      <SelectItem value="monthly">Every month</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('frequency') !== 'none' && (
              <FormField
                control={form.control}
                name="interval"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Interval</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        className="h-10"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Repeat every {field.value}{' '}
                      {form.watch('frequency') === 'hourly'
                        ? 'hour(s)'
                        : form.watch('frequency') === 'daily'
                          ? 'day(s)'
                          : form.watch('frequency') === 'weekly'
                            ? 'week(s)'
                            : 'month(s)'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Date Range Configuration */}
          <div className="space-y-4">
            <div className="border-b pb-2">
              <h3 className="text-lg font-medium">Date Range (Optional)</h3>
              <p className="text-muted-foreground text-sm">Limit when this schedule is active</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} className="h-10" />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Leave empty to start immediately
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} className="h-10" />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Leave empty for no end date
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="space-y-4">
            <div className="border-b pb-2">
              <h3 className="text-lg font-medium">Advanced Settings</h3>
              <p className="text-muted-foreground text-sm">Priority and playback behavior</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Priority Level</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={1000}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 100)}
                        className="h-10"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Higher numbers = higher priority (1-1000)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="forceInterrupt"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium">Force Interrupt</FormLabel>
                        <FormDescription className="text-xs">
                          Stop current playback to play this message
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium">Enabled</FormLabel>
                        <FormDescription className="text-xs">
                          Schedule is active and will trigger
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditing ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}

interface ScheduleCardProps {
  schedule: any
  onEdit: () => void
  onDelete: () => void
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({ schedule, onEdit, onDelete }) => {
  const nextOccurrence = schedule.nextOccurrence ? parseISO(schedule.nextOccurrence) : null

  const getFrequencyIcon = (frequency: string) => {
    if (frequency.toLowerCase().includes('hourly')) return '🕐'
    if (frequency.toLowerCase().includes('daily')) return '📅'
    if (frequency.toLowerCase().includes('weekly')) return '📆'
    if (frequency.toLowerCase().includes('monthly')) return '🗓️'
    return '📍'
  }

  return (
    <Card className="transition-all hover:shadow-md">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <span className="text-lg font-semibold">
                {schedule.startTime} - {schedule.endTime}
              </span>
              <span className="text-lg">{getFrequencyIcon(schedule.frequencyDescription)}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {schedule.frequencyDescription}
              </Badge>
              <Badge variant="outline" className="text-xs">
                Priority: {schedule.priority}
              </Badge>
              {!schedule.enabled && (
                <Badge variant="secondary" className="text-xs">
                  Disabled
                </Badge>
              )}
              {schedule.forceInterrupt && (
                <Badge variant="destructive" className="text-xs">
                  Force Interrupt
                </Badge>
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Date Range */}
          {(schedule.startDate || schedule.endDate) && (
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-muted-foreground space-y-1 text-sm">
                {schedule.startDate && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Start:</span>
                    {format(parseISO(schedule.startDate), 'MMM d, yyyy')}
                  </div>
                )}
                {schedule.endDate && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">End:</span>
                    {format(parseISO(schedule.endDate), 'MMM d, yyyy')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Occurrence */}
          <div className="space-y-2">
            {nextOccurrence && schedule.enabled && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-green-700">
                <Play className="h-4 w-4" />
                <div>
                  <div className="text-sm font-medium">Next occurrence</div>
                  <div className="text-xs">{format(nextOccurrence, "EEEE, MMM d 'at' h:mm a")}</div>
                </div>
              </div>
            )}

            {!nextOccurrence && schedule.enabled && (
              <div className="flex items-center gap-2 rounded-lg bg-yellow-50 p-3 text-yellow-700">
                <AlertTriangle className="h-4 w-4" />
                <div>
                  <div className="text-sm font-medium">No upcoming occurrences</div>
                  <div className="text-xs">Check your date range and frequency settings</div>
                </div>
              </div>
            )}

            {!schedule.enabled && (
              <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-3 text-gray-600">
                <Pause className="h-4 w-4" />
                <div>
                  <div className="text-sm font-medium">Schedule disabled</div>
                  <div className="text-xs">Enable to start scheduling</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const VoiceMessageSchedulesScreen = () => {
  const navigate = useNavigate()
  const { voiceMessageId } = useParams()
  invariant(voiceMessageId)

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<any>(null)
  const [deletingSchedule, setDeletingSchedule] = useState<any>(null)

  const {
    data: vmData,
    loading: vmLoading,
    error: vmError,
  } = useQuery(VOICE_MESSAGE_QUERY, {
    variables: { id: voiceMessageId },
  })

  const {
    data: schedulesData,
    loading: schedulesLoading,
    refetch: refetchSchedules,
  } = useQuery(VOICE_MESSAGE_SCHEDULES_QUERY, {
    variables: { voiceMessageId, enabledOnly: false },
    skip: !voiceMessageId,
  })

  const [deleteSchedule] = useSafeMutation(DELETE_VOICE_MESSAGE_SCHEDULE_MUTATION)

  const voiceMessage = vmData?.voiceMessage
  const schedules = schedulesData?.voiceMessageSchedules || []

  const handleCreateSuccess = () => {
    setShowCreateDialog(false)
    refetchSchedules()
  }

  const handleEditSuccess = () => {
    setEditingSchedule(null)
    refetchSchedules()
  }

  const handleDelete = async (schedule: any) => {
    try {
      await deleteSchedule({
        variables: {
          input: {
            id: schedule.id,
          },
        },
        refetchQueries: [VOICE_MESSAGE_SCHEDULES_QUERY as any],
      })
      setDeletingSchedule(null)
    } catch (error) {
      console.error('Error deleting schedule:', error)
    }
  }

  if (vmLoading || schedulesLoading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="flex-shrink-0 border-b p-6">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Voice Messages
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-4xl">
            <div className="animate-pulse space-y-4">
              <div className="h-8 w-1/3 rounded bg-gray-200"></div>
              <div className="h-4 w-2/3 rounded bg-gray-200"></div>
              <Card>
                <CardHeader>
                  <div className="h-6 w-1/2 rounded bg-gray-200"></div>
                  <div className="h-4 w-1/3 rounded bg-gray-200"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-4 w-full rounded bg-gray-200"></div>
                    <div className="h-4 w-3/4 rounded bg-gray-200"></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (vmError || !voiceMessage) {
    return (
      <div className="flex h-screen flex-col">
        <div className="flex-shrink-0 border-b p-6">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Voice Messages
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-4xl py-12 text-center">
            <p className="text-red-600">
              {vmError ? `Error: ${vmError.message}` : 'Voice message not found'}
            </p>
            <Button onClick={() => navigate(voiceMessagesPath({}))} className="mt-4">
              Back to Voice Messages
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex-shrink-0 border-b p-6">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Voice Messages
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6">
            <h1 className="mb-2 text-2xl font-bold">Voice Message Schedules</h1>
            <p className="text-muted-foreground">
              Manage when "{voiceMessage.displayName}" will be played
            </p>

            {/* Audio Preview */}
            {voiceMessage.audioUrl && voiceMessage.ttsStatus === 'completed' && (
              <div className="bg-muted/10 mt-4 rounded-lg border p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <Volume2 className="h-4 w-4" />
                  Preview Message
                </div>
                <AudioPlayer
                  audioUrl={voiceMessage.audioUrl}
                  title={`"${voiceMessage.text.substring(0, 100)}${voiceMessage.text.length > 100 ? '...' : ''}"`}
                />
              </div>
            )}
          </div>

          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Schedules ({schedules.length})</h2>
              <p className="text-muted-foreground text-sm">
                {schedules.filter((s: any) => s.enabled).length} active,{' '}
                {schedules.filter((s: any) => !s.enabled).length} disabled
              </p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  New Schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] max-w-4xl">
                <DialogHeader>
                  <DialogTitle>Create New Schedule</DialogTitle>
                  <DialogDescription>
                    Set up when this voice message should be played
                  </DialogDescription>
                </DialogHeader>
                <ScheduleForm
                  voiceMessageId={voiceMessageId}
                  onSuccess={handleCreateSuccess}
                  onCancel={() => setShowCreateDialog(false)}
                />
              </DialogContent>
            </Dialog>
          </div>

          {schedules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No schedules yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first schedule to start playing this voice message at specific times.
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {schedules.map((schedule: any) => (
                <ScheduleCard
                  key={schedule.id}
                  schedule={schedule}
                  onEdit={() => setEditingSchedule(schedule)}
                  onDelete={() => setDeletingSchedule(schedule)}
                />
              ))}
            </div>
          )}

          {/* Edit Dialog */}
          <Dialog open={!!editingSchedule} onOpenChange={() => setEditingSchedule(null)}>
            <DialogContent className="max-h-[90vh] max-w-4xl">
              <DialogHeader>
                <DialogTitle>Edit Schedule</DialogTitle>
                <DialogDescription>Update the schedule settings</DialogDescription>
              </DialogHeader>
              {editingSchedule && (
                <ScheduleForm
                  voiceMessageId={voiceMessageId}
                  schedule={editingSchedule}
                  onSuccess={handleEditSuccess}
                  onCancel={() => setEditingSchedule(null)}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={!!deletingSchedule} onOpenChange={() => setDeletingSchedule(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Schedule</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this schedule? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDeletingSchedule(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deletingSchedule && handleDelete(deletingSchedule)}
                >
                  Delete Schedule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
